import { Loader } from '@wheely/ui-kit';
import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { FsmState, TFsmEffects, TFsmEvent, TFsmState } from '@/modules/onboarding/fsm-types';
import { fsmSelectors } from '@/modules/onboarding/store/fsm-slice';
import { TCountryCodes } from '@/shared/models/country-code';
import { TTransition } from '@/shared/store/finite-state-machine/types';
import { StepLayout } from '@/shared/view/StepLayout';
import { UploadCar, UploadMyDriver, UploadPartner } from '@/shared/view/Upload';

import { DoYouHaveACar } from './components/DoYouHaveACar';
import { Driver } from './components/Driver';
import { LicensingAuthority as StepLicensingAuthority } from './components/LicensingAuthority';
import { LicensingAuthorityMissing as StepLicensingAuthorityMissing } from './components/LicensingAuthorityMissing';
import { StepPayments } from './components/Payments';
import { PaymentType } from './components/PaymentType';
import { Vehicle } from './components/Vehicle';
import { VehicleSelection as StepVehicleSelection } from './components/VehicleSelection';
import { Welcome } from './components/Welcome';
import s from './styles.module.css';
import {
  mapFsmStateToCtProps,
  mapFsmStateToCtPropsFR,
  mapFsmStateToCtPropsUK,
  mapFsmStateToCtPropsUS,
} from './view-config';

interface IPropsView {
  onSwitchScreen: (event: TFsmEvent) => Promise<unknown>;
  internalPath: TFsmState;
  partnerFirstName: string;
  partnerCountryCode?: TCountryCodes;
}

const ignoreQueryParams = (url: string): string => url.split('?')[0];

const getQueryParam = (url: string, key: string): string | undefined => {
  const params = new URLSearchParams(url.split('?')[1]);

  return params.get(key) ?? undefined;
};

const createHandlers =
  (onSwitchScreen: (event: TFsmEvent) => Promise<unknown>) =>
  (transitions: TTransition<TFsmState, TFsmEvent, TFsmEffects>) => ({
    onClickYes: 'YES' in transitions ? () => onSwitchScreen('YES') : undefined,
    onClickNo: 'NO' in transitions ? () => onSwitchScreen('NO') : undefined,
    onClickBack: 'BACK' in transitions ? () => onSwitchScreen('BACK') : undefined,
  });

const WizardView = React.memo(
  ({ internalPath, partnerFirstName, partnerCountryCode, onSwitchScreen }: IPropsView) => {
    const intl = useIntl();
    const transitions = useSelector(fsmSelectors.getTransitions);
    const isInitialized = useSelector(fsmSelectors.isInitialized);
    const handlers = useMemo(
      () => createHandlers(onSwitchScreen)(transitions),
      [onSwitchScreen, transitions],
    );
    const handleClickBack = useCallback(() => onSwitchScreen('BACK'), [onSwitchScreen]);

    const containerProps = useMemo(() => {
      if (partnerCountryCode === 'GB') {
        return mapFsmStateToCtPropsUK({ stateValue: internalPath, intl });
      }

      if (partnerCountryCode === 'FR') {
        return mapFsmStateToCtPropsFR({ stateValue: internalPath, intl });
      }

      if (partnerCountryCode === 'US') {
        return mapFsmStateToCtPropsUS({ stateValue: internalPath, intl });
      }

      return mapFsmStateToCtProps({ partnerFirstName, stateValue: internalPath, intl });
    }, [internalPath, intl, partnerFirstName, partnerCountryCode]);

    const internalPathOnly = ignoreQueryParams(internalPath);

    if (!isInitialized) {
      return (
        <div className={s.loader}>
          <Loader />
        </div>
      );
    }

    return (
      <StepLayout
        key="swap-container"
        {...containerProps}
        transitionKey={internalPathOnly}
        {...handlers}
      >
        {(() => {
          const nodes: React.ReactNode[] = [];

          switch (internalPathOnly) {
            case FsmState.welcome:
              nodes.push(<Welcome key="welcome" onSwitchScreen={onSwitchScreen} />);

              break;
            case FsmState.driver:
              nodes.push(<Driver key="driver" onSwitchScreen={onSwitchScreen} />);

              break;
            case FsmState.newCar:
              nodes.push(<Vehicle key="new-car" onSwitchScreen={onSwitchScreen} />);

              break;
            case FsmState.doYouHaveCar:
              nodes.push(<DoYouHaveACar key="have-car" />);

              break;
            case FsmState.payTo:
              nodes.push(<PaymentType key="pay-to" onSwitchScreen={onSwitchScreen} />);

              break;
            case FsmState.partnerPayments:
              nodes.push(<StepPayments key="partner" onSwitchScreen={onSwitchScreen} />);

              break;
            case FsmState.licensingAuthority:
              nodes.push(
                <StepLicensingAuthority key="licensingAuthority" onSwitchScreen={onSwitchScreen} />,
              );

              break;

            case FsmState.licensingAuthorityMissing:
              nodes.push(<StepLicensingAuthorityMissing key="licensingAuthorityMissing" />);

              break;
            case FsmState.vehicle:
              nodes.push(<StepVehicleSelection key="vehicle" onSwitchScreen={onSwitchScreen} />);

              break;
            default:
              break;
          }

          const upload = getQueryParam(internalPath, 'upload');

          switch (upload) {
            case 'driver':
              nodes.push(
                <UploadMyDriver
                  key="driver-upload"
                  onClickSkip={handleClickBack}
                  onFinishUpload={handleClickBack}
                />,
              );

              break;
            case 'car':
              nodes.push(
                <UploadCar
                  key="car-upload"
                  onClickSkip={handleClickBack}
                  onFinishUpload={handleClickBack}
                />,
              );

              break;
            case 'legal':
              nodes.push(
                <UploadPartner
                  key="legal-upload"
                  onClickSkip={handleClickBack}
                  onFinishUpload={handleClickBack}
                />,
              );

              break;
            default:
              break;
          }

          return nodes;
        })()}
      </StepLayout>
    );
  },
);

WizardView.displayName = 'WizardView';

export { WizardView };
